import { AssignActorRoleRequest, RoleResponse } from '@flatfile/api-internal/api'
import {
  Alert,
  AlertDescription,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@flatfile/baseline'
import { isEqual } from 'moderndash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { UserRole, UserWithRoles } from '../../../api/internal/users'
import { useIdentity } from '../../../hooks/useIdentity'
import { useCollection } from '../../../resources/hooks/useCollection'
import { useCreateResource, useUpdateResource } from '../../../resources/hooks/useResourceMutation'

interface BaseProps {
  mode: 'edit' | 'invite'
}

interface InviteUserModalProps extends BaseProps {
  mode: 'invite'
  user?: never // user should not be provided in invite mode
  onClose: () => void
  isOnlyAccountAdmin: false
}

interface EditUserModalProps extends BaseProps {
  mode: 'edit'
  user: UserWithRoles // user is required in edit mode
  onClose: () => void
  isOnlyAccountAdmin?: boolean
}

type UserModalProps = InviteUserModalProps | EditUserModalProps
export interface ErrorDetail {
  key: string
  message: string
}

const roleExists = (role: UserRole, roles: UserRole[]) =>
  roles.some((existingRole) => existingRole.id === role.id && existingRole.resourceId === role.resourceId)

const getRoleDescription = (role: RoleResponse) => {
  switch (role.name) {
    case 'Admin':
      return 'Admins have full access to all resources and can manage users, roles, billing, data retention policies and secrets.'
    case 'User':
      return 'Users have full access to Apps, Environments and Spaces. They do not have access to users, billing or secrets.'
    case 'Guest':
      return 'Guests have read and update permissions on Spaces. They have read only access on Apps and no access on Environments.'
    default:
      return undefined
  }
}

const defaultError = [{ key: 'unknown', message: 'An unexpected error occurred. Please try again later.' }]

export const UserModal = ({ user, onClose, mode, isOnlyAccountAdmin }: UserModalProps) => {
  const { identity } = useIdentity()
  const [isNameModified, setIsNameModified] = useState(false)
  const [isRoleModified, setIsRoleModified] = useState(false)
  const [email, setEmail] = useState(user?.email ?? '')
  const [name, setName] = useState(user?.name ?? '')
  const [errors, setErrors] = useState<ErrorDetail[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedRole, setSelectedRole] = useState<string | null>(null)
  const [selectedResource, setSelectedResource] = useState<string | null>(null)
  const [selectedUserRoles, setSelectedUserRoles] = useState(user?.roles ?? [])
  const [addRoles, setAddRoles] = useState(false)
  const environments = useCollection('ENVIRONMENTS')
  const spaces = useCollection('SPACES', { pageSize: 1000, filterByEnvId: false }) // we want to fetch ALL spaces
  const roles = useCollection('ROLES')
  const [updateUser] = useUpdateResource('USERS')
  const [createUser] = useCreateResource('USERS')
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false)
  const [resourceDropdownOpen, setResourceDropdownOpen] = useState(false)
  const [resourceSearchTerm, setResourceSearchTerm] = useState('')

  const rolesItems = useMemo(
    () =>
      roles
        .map((role) => ({ value: role.id, label: role.name, tooltip: getRoleDescription(role) }))
        .filter((role) => role.label !== 'SheetGuest'),
    [roles]
  )

  const disableSubmit = useMemo(() => {
    if (mode === 'edit') return !name || selectedUserRoles.length === 0 || (!isNameModified && !isRoleModified)
    if (mode === 'invite') return !name || !email || selectedUserRoles.length === 0
  }, [name, email, selectedUserRoles, isNameModified, isRoleModified])

  useEffect(() => {
    if (mode === 'edit') {
      const nameChanged = name !== user?.name
      const rolesChanged = !isEqual(user?.roles, selectedUserRoles)
      setIsNameModified(nameChanged)
      setIsRoleModified(rolesChanged)
    }
  }, [name, selectedUserRoles, user?.name, user?.roles, mode])

  const handleUpdate = async () => {
    setLoading(true)
    // Identify roles added to the user
    const addedRoles = selectedUserRoles
      .filter((currentRole) => !roleExists(currentRole, user!.roles))
      .map((role) => ({
        id: role.id,
        resourceId: role.resourceId,
      }))

    // Identify roles removed from the user
    const deletedRoles = user!.roles
      .filter((originalRole) => !roleExists(originalRole, selectedUserRoles))
      .map((role) => ({
        actorRoleId: role.actorRoleId,
      }))

    const body = {
      id: user!.id,
      ...(isNameModified && { name }),
      ...(isRoleModified && { roles: { addedRoles, deletedRoles } }),
    }
    try {
      await updateUser(body)
      setLoading(false)
      onClose()
    } catch (e: any) {
      setLoading(false)
      setErrors(e.data?.errors || defaultError)
    }
  }

  const handleInvite = async () => {
    setLoading(true)
    const actorRoles: AssignActorRoleRequest[] = selectedUserRoles.map((role) => ({
      roleId: role.id,
      resourceId: role.resourceId,
    }))
    const body = {
      email,
      name,
      actorRoles,
    }
    try {
      await createUser(body)
      setLoading(false)
      onClose()
    } catch (e: any) {
      setLoading(false)
      setErrors(e.data?.errors || defaultError)
    }
  }

  const openAddRole = () => {
    setAddRoles(true)
  }

  const handleAddRole = () => {
    if (selectedRole && selectedResource) {
      const newRole = {
        id: selectedRole,
        name: roles.find((role) => role.id === selectedRole)!.name,
        resourceId: selectedResource,
        resourceName: selectedResource.includes('_acc_')
          ? 'Account'
          : environments.find((env) => env.id === selectedResource)?.name ||
            spaces.find((space) => space.id === selectedResource)?.name ||
            'unknown',
        actorRoleId: '',
      }
      setSelectedUserRoles([...selectedUserRoles, newRole])
      setSelectedRole(null)
      setSelectedResource(null)
    }
  }

  const handleDelete = (roleToRemove: {
    id?: string
    name?: string
    resourceId: any
    resourceName?: string | undefined
    actorRoleId: string
  }) => {
    const updatedUserRoles = selectedUserRoles.filter(
      (userRole) => !(userRole.id === roleToRemove.id && userRole.resourceId === roleToRemove.resourceId)
    )
    setSelectedUserRoles(updatedUserRoles)
  }

  const handleSubmit = mode === 'edit' ? handleUpdate : handleInvite

  const RoleRows = selectedUserRoles.map((role, index) => {
    const disableDelete = !!(isOnlyAccountAdmin && role.name === 'Admin' && role.resourceName === 'Account')

    return (
      <TableRow key={index} className='hover:bg-gray-50 border-b border-gray-200 last:border-b-0'>
        <TableCell className='p-2 text-sm'>{role.name}</TableCell>
        <TableCell className='p-2 text-sm'>{role.resourceName}</TableCell>
        <TableCell className='p-2 text-sm' style={{ width: '1px', whiteSpace: 'nowrap' }}>
          {disableDelete ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button className='p-1 rounded-sm text-gray-400 cursor-not-allowed' disabled>
                  <Icon name='trash-can-2' size={16} />
                </button>
              </TooltipTrigger>
              <TooltipContent className='flex items-center px-2 py-1 text-xs max-w-[300px]'>
                <Text variant='body' className='text-sm'>
                  There must be at least one Account Admin on the account
                </Text>
              </TooltipContent>
            </Tooltip>
          ) : (
            <button className='p-1 rounded-sm hover:bg-gray-100 text-gray-500' onClick={() => handleDelete(role)}>
              <Icon name='trash-can-2' size={16} />
            </button>
          )}
        </TableCell>
      </TableRow>
    )
  })

  const getSelectData = useCallback(() => {
    const spacesItems = spaces.map((space) => ({
      value: space.id,
      label: `${space.name} - ${space.id}`,
      environmentId: space.environmentId,
      disabled: selectedUserRoles.some((role) => role.resourceId === space.id),
    }))

    const environmentsItems = environments.map((env) => ({
      value: env.id,
      label: env.name,
      disabled: selectedUserRoles.some((role) => role.resourceId === env.id),
    }))

    const accountItems = [
      {
        value: identity.account.id,
        label: 'Account',
        disabled: selectedUserRoles.some((role) => role.resourceId.includes('_acc_')),
      },
    ]

    // Filter function that searches both name and ID
    const filterBySearch = (item: { value: string; label: string; disabled?: boolean }) => {
      if (!resourceSearchTerm.trim()) return true
      const searchLower = resourceSearchTerm.toLowerCase()
      return item.label.toLowerCase().includes(searchLower) || item.value.toLowerCase().includes(searchLower)
    }

    const envsItems = environmentsItems
      .map((env) => ({
        value: env.value,
        label: env.label,
        disabled: env.disabled,
      }))
      .filter(filterBySearch)

    const spacesGroupedByEnv = environmentsItems.reduce(
      (acc, env) => {
        const envSpaces = spacesItems
          .filter((space) => space.environmentId === env.value)
          .map((space) => ({
            value: space.value,
            label: space.label,
            disabled: space.disabled,
          }))
          .filter(filterBySearch)

        if (envSpaces.length > 0) {
          acc[`Spaces - ${env.label}`] = envSpaces
        }

        return acc
      },
      {} as Record<string, { value: string; label: string; disabled?: boolean }[]>
    )

    return {
      account: accountItems.filter(filterBySearch),
      environments: envsItems,
      spaces: spacesGroupedByEnv,
    }
  }, [identity, environments, spaces, selectedUserRoles, resourceSearchTerm])

  const selectData = getSelectData()

  // Role dropdown handler
  const handleRoleSelect = (roleId: string) => {
    setSelectedRole(roleId)
  }

  // Resource dropdown handler
  const handleResourceSelect = (resourceId: string) => {
    setSelectedResource(resourceId)
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col gap-2'>
        <div className='space-y-1'>
          <Label htmlFor='email'>Email *</Label>
          <Input
            id='email'
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={mode === 'edit'}
            className='text-sm bg-base-white'
          />
        </div>

        <div className='space-y-1'>
          <Label htmlFor='name'>Name *</Label>
          <Input id='name' required value={name} onChange={(e) => setName(e.target.value)} className='text-sm' />
        </div>

        <div className='space-y-1 mt-2'>
          <Label>User role(s) *</Label>
          <Table className='border border-gray-200 rounded'>
            <TableHeader className='bg-gray-50 border-b border-gray-200'>
              <TableRow>
                <TableHead className='p-2 text-left font-medium text-gray-600'>Type</TableHead>
                <TableHead className='p-2 text-left font-medium text-gray-600'>Resource</TableHead>
                <TableHead className='p-2 text-left font-medium text-gray-600'></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>{RoleRows}</TableBody>
          </Table>

          {!addRoles && (
            <Button
              variant='secondary'
              size='sm'
              className='w-fit flex items-center gap-1 mt-2'
              onClick={openAddRole}
              icon='plus-small'
              iconProps={{ size: 12 }}
            >
              Add new role
            </Button>
          )}

          {addRoles && (
            <div className='border-l-2 border-gray-200 pl-4 mt-3 space-y-3'>
              <div className='space-y-1'>
                <Label htmlFor='role'>Role *</Label>
                <DropdownMenu open={roleDropdownOpen} onOpenChange={setRoleDropdownOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button variant='outline' className='w-full justify-between text-left font-normal' size='sm'>
                      <span>
                        {selectedRole ? rolesItems.find((r) => r.value === selectedRole)?.label : 'Select a role'}
                      </span>
                      <Icon
                        name='chevron-bottom'
                        size={12}
                        className={
                          roleDropdownOpen ? 'transform rotate-180 transition-transform' : 'transition-transform'
                        }
                      />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className='w-full min-w-[240px] z-[100]' align='start' sideOffset={4}>
                    {rolesItems.map((role) => {
                      if (!role.tooltip) {
                        return (
                          <DropdownMenuItem
                            key={role.value}
                            onSelect={() => {
                              handleRoleSelect(role.value)
                              setRoleDropdownOpen(false)
                            }}
                            className='focus:bg-gray-100 focus:text-primary hover:bg-gray-100 hover:text-primary'
                          >
                            {role.label}
                          </DropdownMenuItem>
                        )
                      }

                      return (
                        <Tooltip delayDuration={300} key={role.value}>
                          <TooltipTrigger asChild>
                            <DropdownMenuItem
                              onSelect={() => {
                                handleRoleSelect(role.value)
                                setRoleDropdownOpen(false)
                              }}
                              className='focus:bg-gray-100 focus:text-primary hover:bg-gray-100 hover:text-primary'
                            >
                              {role.label}
                            </DropdownMenuItem>
                          </TooltipTrigger>
                          {role.tooltip && (
                            <TooltipContent
                              side='right'
                              sideOffset={5}
                              className='flex items-center px-2 py-1 text-xs max-w-[350px] border border-gray-200 shadow-sm z-[101]'
                            >
                              <Text variant='body' className='text-sm'>
                                {role.tooltip}
                              </Text>
                            </TooltipContent>
                          )}
                        </Tooltip>
                      )
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className='space-y-1'>
                <Label htmlFor='resource'>Resource *</Label>
                <DropdownMenu open={resourceDropdownOpen} onOpenChange={setResourceDropdownOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button variant='outline' className='w-full justify-between text-left font-normal' size='sm'>
                      <span>
                        {selectedResource
                          ? selectData.account.find((a) => a.value === selectedResource)?.label ||
                            selectData.environments.find((e) => e.value === selectedResource)?.label ||
                            Object.entries(selectData.spaces)
                              .flatMap(([_, items]) => items)
                              .find((s) => s.value === selectedResource)?.label ||
                            'Select a resource'
                          : 'Select a resource'}
                      </span>
                      <Icon
                        name='chevron-bottom'
                        size={12}
                        className={
                          resourceDropdownOpen ? 'transform rotate-180 transition-transform' : 'transition-transform'
                        }
                      />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    className='w-full min-w-[240px] max-h-[300px] z-[100] p-0'
                    align='start'
                    sideOffset={4}
                  >
                    <div className='flex items-center h-9 px-1 border-b border-gray-200'>
                      <input
                        className='bg-base-white flex-grow pl-2 text-[13px] leading-9 outline-none'
                        type='text'
                        value={resourceSearchTerm}
                        onChange={(e) => setResourceSearchTerm(e.target.value)}
                        placeholder='Search by name or ID...'
                        autoFocus
                      />
                      <button
                        className='bg-base-white border-0 h-full px-2 text-gray-500 hover:text-gray-700'
                        onClick={() => setResourceSearchTerm('')}
                      >
                        <Icon name='cross-small' />
                      </button>
                    </div>

                    <div className='overflow-y-auto max-h-[250px]'>
                      {selectData.account.length > 0 && (
                        <>
                          <DropdownMenuGroup>
                            <DropdownMenuLabel>Account</DropdownMenuLabel>
                            {selectData.account.map((item) => (
                              <DropdownMenuItem
                                key={item.value}
                                onSelect={() => {
                                  handleResourceSelect(item.value)
                                  setResourceDropdownOpen(false)
                                  setResourceSearchTerm('')
                                }}
                                disabled={item.disabled}
                                className='focus:bg-gray-100 focus:text-primary hover:bg-gray-100 hover:text-primary disabled:opacity-50'
                              >
                                {item.label}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuGroup>
                          {(selectData.environments.length > 0 || Object.keys(selectData.spaces).length > 0) && (
                            <DropdownMenuSeparator />
                          )}
                        </>
                      )}

                      {selectData.environments.length > 0 && (
                        <>
                          <DropdownMenuGroup>
                            <DropdownMenuLabel>Environments</DropdownMenuLabel>
                            {selectData.environments.map((item) => (
                              <DropdownMenuItem
                                key={item.value}
                                onSelect={() => {
                                  handleResourceSelect(item.value)
                                  setResourceDropdownOpen(false)
                                  setResourceSearchTerm('')
                                }}
                                disabled={item.disabled}
                                className='focus:bg-gray-100 focus:text-primary hover:bg-gray-100 hover:text-primary disabled:opacity-50'
                              >
                                {item.label}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuGroup>
                          {Object.keys(selectData.spaces).length > 0 && <DropdownMenuSeparator />}
                        </>
                      )}

                      {Object.entries(selectData.spaces).map(([groupName, items]) => (
                        <DropdownMenuGroup key={groupName}>
                          <DropdownMenuLabel>{groupName}</DropdownMenuLabel>
                          {items.map((item) => (
                            <DropdownMenuItem
                              key={item.value}
                              onSelect={() => {
                                handleResourceSelect(item.value)
                                setResourceDropdownOpen(false)
                                setResourceSearchTerm('')
                              }}
                              disabled={item.disabled}
                              className='focus:bg-gray-100 focus:text-primary hover:bg-gray-100 hover:text-primary disabled:opacity-50'
                            >
                              {item.label}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuGroup>
                      ))}

                      {selectData.account.length === 0 &&
                        selectData.environments.length === 0 &&
                        Object.keys(selectData.spaces).length === 0 && (
                          <div className='p-2 text-sm text-gray-500 text-center'>
                            No resources found matching "{resourceSearchTerm}"
                          </div>
                        )}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className='flex gap-2 mt-3'>
                <Button
                  variant='primary'
                  size='sm'
                  onClick={handleAddRole}
                  disabled={!selectedResource || !selectedRole}
                >
                  Add role
                </Button>
                <Button variant='ghost' size='sm' onClick={() => setAddRoles(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Button variant='primary' className='w-full mt-3' onClick={handleSubmit} disabled={disableSubmit || loading}>
        {loading ? (
          <div className='flex items-center gap-2'>
            <span className='animate-spin'>&#8635;</span>
            {mode === 'invite' ? 'Inviting...' : 'Updating...'}
          </div>
        ) : mode === 'invite' ? (
          'Invite User'
        ) : (
          'Update User'
        )}
      </Button>

      {errors.length > 0 && (
        <Alert variant='destructive' className='mt-3'>
          <AlertDescription>{errors.map((err) => err.message).join(', ')}</AlertDescription>
        </Alert>
      )}
    </div>
  )
}
